/**
 * Real-time Events Context
 * 
 * Provides a single persistent WebSocket connection for the entire application.
 * The connection stays alive across page navigation and only disconnects on logout.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Event types matching backend schema
export type EventType =
  | 'router_online'
  | 'router_offline'
  | 'router_metrics'
  | 'user_connected'
  | 'user_disconnected'
  | 'session_started'
  | 'session_ended'
  | 'session_paused'
  | 'session_resumed'
  | 'plan_purchased'
  | 'voucher_redeemed'
  | 'coin_inserted'
  | 'high_temperature'
  | 'high_cpu_usage'
  | 'high_memory_usage'
  | 'activity_list'
  | 'recent_routers_list'
  | 'recent_online_users_list';

// Base event interface
export interface BaseEvent {
  eventType: EventType;
  routerId: string;
  routerName?: string;
  location?: string;
  agentId?: string;
  timestamp: string;
}

// Re-export all event types from useRealTimeEvents
export type RealTimeEvent = any; // Will match all event types

// Connection state
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface RealTimeEventsContextType {
  events: RealTimeEvent[];
  connectionState: ConnectionState;
  isConnected: boolean;
  subscribe: (callback: (event: RealTimeEvent) => void) => () => void;
  clearEvents: () => void;
  reconnect: () => void;
}

const RealTimeEventsContext = createContext<RealTimeEventsContextType | undefined>(undefined);

/**
 * Get WebSocket URL based on environment
 */
function getWebSocketUrl(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
  
  if (apiUrl.startsWith('/')) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProtocol}://${window.location.host}/ws`;
  }
  
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '');
  return `${wsProtocol}://${baseUrl}/ws`;
}

interface RealTimeEventsProviderProps {
  children: React.ReactNode;
  maxEvents?: number;
  autoReconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export function RealTimeEventsProvider({
  children,
  maxEvents = 100,
  autoReconnect = true,
  reconnectDelay = 3000,
  maxReconnectAttempts = 10,
}: RealTimeEventsProviderProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const subscribersRef = useRef<Set<(event: RealTimeEvent) => void>>(new Set());

  // Add event to state and notify subscribers
  const addEvent = useCallback((event: RealTimeEvent) => {
    setEvents((prev) => {
      const newEvents = [event, ...prev];
      return newEvents.slice(0, maxEvents);
    });

    // Notify all subscribers
    subscribersRef.current.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[RealTime] Subscriber callback error:', error);
      }
    });
  }, [maxEvents]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!user) {
      console.log('[RealTime] No user authenticated, skipping connection');
      return;
    }

    // Prevent multiple simultaneous connections
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      console.log('[RealTime] Already connected or connecting, skipping');
      return;
    }

    const token = localStorage.getItem('sharewifi_auth');
    if (!token) {
      console.warn('[RealTime] No auth token available');
      setConnectionState('error');
      return;
    }

    let authToken: string;
    try {
      const parsed = JSON.parse(token);
      authToken = parsed.token;
    } catch {
      console.warn('[RealTime] Invalid auth token format');
      setConnectionState('error');
      return;
    }

    // Close any existing connection first
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(authToken)}`;
    console.log('[RealTime] Connecting to WebSocket');
    setConnectionState('connecting');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[RealTime] WebSocket connected');
        reconnectAttemptsRef.current = 0;
        setConnectionState('connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RealTimeEvent;
          console.log('[RealTime] Received event:', data.eventType, data);
          addEvent(data);
        } catch (error) {
          console.error('[RealTime] Failed to parse event:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[RealTime] WebSocket error:', error);
        setConnectionState('error');
      };

      ws.onclose = (event) => {
        console.log('[RealTime] WebSocket closed:', event.code, event.reason);
        wsRef.current = null;
        setConnectionState('disconnected');

        // Auto-reconnect if enabled and user is still logged in
        if (autoReconnect && user && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          console.log(
            `[RealTime] Reconnecting in ${reconnectDelay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
          );
          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      };
    } catch (error) {
      console.error('[RealTime] Failed to create WebSocket:', error);
      setConnectionState('error');
    }
  }, [user, addEvent, autoReconnect, maxReconnectAttempts, reconnectDelay]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  // Reconnect WebSocket
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  // Subscribe to events
  const subscribe = useCallback((callback: (event: RealTimeEvent) => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  // Clear events
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Connect when user logs in, disconnect when user logs out
  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      // Only clean up timeouts, don't disconnect on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [user]); // Only reconnect when user changes (login/logout)

  const value: RealTimeEventsContextType = {
    events,
    connectionState,
    isConnected: connectionState === 'connected',
    subscribe,
    clearEvents,
    reconnect,
  };

  return (
    <RealTimeEventsContext.Provider value={value}>
      {children}
    </RealTimeEventsContext.Provider>
  );
}

/**
 * Hook to access real-time events context
 */
export function useRealTimeEventsContext() {
  const context = useContext(RealTimeEventsContext);
  if (!context) {
    throw new Error('useRealTimeEventsContext must be used within RealTimeEventsProvider');
  }
  return context;
}
